import { useMemo, useState } from 'react';
import { Modal } from '../components/Modal';
import { api, ApiError, newIdempotencyKey, type WithdrawMethod } from '../lib/api';

type Props = {
    balance: number;
    /** Withdraw floor (BSP) — comes from root-tunable settings.withdraw_min. */
    minAmount?: number;
    onClose: () => void;
    /** Called after the backend confirms the withdrawal was created. */
    onSubmitted: (info: { amount: number; method: WithdrawMethod; balance: number | null }) => void;
};

const FALLBACK_MIN_AMOUNT = 500;
/** Default tech-fee disclaimer the spec asks us to surface. */
const FEE_NOTICE = 'Оплата технической комиссии будет на вашей стороне. Заранее размер неизвестен.';
/** Stars hold reminder per выводы.md. */
const STARS_NOTICE =
    'Вывод будет осуществлён на ваш текущий профиль, привязанный к боту. ' +
    'Сроки до 21 дня в первые месяцы — ограничение холда Telegram Stars.';

/**
 * Withdraw / sale flow.
 *
 * Spec rules baked in (see выводы.md):
 *   * Minimum 500 BSP.
 *   * Methods: Stars (на профиль) / TON / CryptoBot (чек) / Gift (бонус).
 *   * Fee disclaimer is always shown.
 *   * Submit hits POST /api/withdrawals via api.createWithdrawal — server
 *     atomically debits the balance, idempotency-key prevents double charge
 *     on retry, and a notification is created so the user sees it in their
 *     bell.
 */
export function WithdrawModal({ balance, minAmount, onClose, onSubmitted }: Props) {
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState<WithdrawMethod>('stars');
    const [target, setTarget] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const min = minAmount && minAmount > 0 ? minAmount : FALLBACK_MIN_AMOUNT;
    const tooLowBalance = balance < min;
    const amountNum = Number.parseFloat(amount.replace(',', '.'));
    const amountValid = Number.isFinite(amountNum) && amountNum >= min && amountNum <= balance;
    const targetValid = useMemo(() => {
        if (method === 'stars') return true;
        if (method === 'cryptobot' || method === 'gift') {
            return /^@?[A-Za-z0-9_]{3,32}$/.test(target.trim());
        }
        if (method === 'ton') return target.trim().length >= 10;
        return false;
    }, [method, target]);

    const canSubmit = !tooLowBalance && amountValid && targetValid && !busy;

    if (tooLowBalance) {
        return (
            <Modal title="Продать BSP" onClose={onClose}>
                <div className="popup-hero">😕</div>
                <p className="popup-text text-center">
                    Ваш баланс слишком низкий для вывода.
                </p>
                <p className="popup-text text-center text-muted">
                    Минимум: <b>{min.toLocaleString('ru-RU')} BSP</b>. Сейчас на счету: <b>{balance.toLocaleString('ru-RU')} BSP</b>.
                </p>
                <button type="button" className="action-btn btn-gold" onClick={onClose}>
                    Понятно
                </button>
            </Modal>
        );
    }

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        setErr(null);
        try {
            const body = {
                amount: Math.floor(amountNum),
                method,
                target:
                    method === 'stars'
                        ? undefined
                        : method === 'ton'
                          ? target.trim()
                          : target.trim().replace(/^@?/, '@'),
            };
            const res = await api.createWithdrawal(newIdempotencyKey(), body);
            onSubmitted({
                amount: body.amount,
                method,
                balance: res.balance ?? null,
            });
        } catch (e) {
            if (e instanceof ApiError) {
                setErr(translateError(e.code));
            } else {
                setErr('Не удалось отправить заявку. Проверьте соединение.');
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal title="Продать BSP" onClose={onClose}>
            <div className="input-group">
                <label>Способ продажи</label>
                <div className="method-pick" style={{ flexWrap: 'wrap' }}>
                    <MethodBtn active={method === 'stars'}     icon="⭐" label="Stars"     onClick={() => setMethod('stars')} />
                    <MethodBtn active={method === 'ton'}       icon="💎" label="TON"       onClick={() => setMethod('ton')} />
                    <MethodBtn active={method === 'cryptobot'} icon="🤖" label="CryptoBot" onClick={() => setMethod('cryptobot')} />
                    <MethodBtn active={method === 'gift'}      icon="🎁" label="Подарок"   onClick={() => setMethod('gift')} />
                </div>
            </div>

            <div className="input-group">
                <label>Сумма (BSP)</label>
                <input
                    type="number"
                    inputMode="decimal"
                    placeholder={`Минимум ${min}`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                />
                <span className="hint">
                    Доступно: <b>{balance.toLocaleString('ru-RU')} BSP</b>
                </span>
            </div>

            {(method === 'cryptobot' || method === 'gift') && (
                <div className="input-group">
                    <label>@username получателя</label>
                    <input
                        type="text"
                        placeholder="@username"
                        value={target}
                        onChange={(e) => setTarget(e.target.value)}
                        autoCapitalize="off"
                    />
                    <span className="hint">
                        {method === 'cryptobot'
                            ? 'Чек CryptoBot придёт пользователю в боте.'
                            : 'Подарок Telegram придёт на этот аккаунт. Username должен быть публичным.'}
                    </span>
                </div>
            )}

            {method === 'ton' && (
                <div className="input-group">
                    <label>Адрес TON-кошелька</label>
                    <input
                        type="text"
                        placeholder="EQC..."
                        value={target}
                        onChange={(e) => setTarget(e.target.value)}
                        autoCapitalize="off"
                    />
                    <span className="hint">
                        Используйте основной адрес кошелька (V4 или Wallet V5).
                    </span>
                </div>
            )}

            {method === 'stars' && (
                <div className="info-card">
                    <p className="popup-text">{STARS_NOTICE}</p>
                </div>
            )}

            <div className="withdraw-warn">{FEE_NOTICE}</div>
            {err && (
                <div className="withdraw-warn" style={{ color: 'var(--red)' }}>{err}</div>
            )}

            <button
                type="button"
                className="action-btn btn-gold"
                disabled={!canSubmit}
                onClick={submit}
            >
                {busy ? 'Отправляем…' : 'Оставить заявку'}
            </button>
        </Modal>
    );
}

function MethodBtn({
    active, icon, label, onClick,
}: {
    active: boolean; icon: string; label: string; onClick: () => void;
}) {
    return (
        <button
            type="button"
            className={`method-btn ${active ? 'active' : ''}`}
            onClick={onClick}
            aria-pressed={active}
        >
            <span className="method-icon">{icon}</span>
            <span>{label}</span>
        </button>
    );
}

function translateError(code: string): string {
    switch (code) {
        case 'insufficient_balance': return 'Недостаточно BSP на балансе.';
        case 'bad_target':           return 'Неверный адрес/username получателя.';
        case 'bad_input':            return 'Сумма ниже минимума или поле заполнено неверно.';
        case 'too_many_requests':    return 'Слишком часто. Подождите пару секунд.';
        case 'auth_required':
        case 'auth_failed':          return 'Сессия истекла, перезайдите в Mini App.';
        default:                     return 'Не удалось создать заявку (' + code + ').';
    }
}
